import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload as UploadIcon, Headphones, X, Image as ImageIcon, Music, ArrowLeft, Plus, GripVertical, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const UploadAudiobook = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    description: '',
    genres: '',
    narrator: '',
  });
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [chapters, setChapters] = useState([{ title: '', file: null }]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChapterTitleChange = (index, value) => {
    const newChapters = [...chapters];
    newChapters[index].title = value;
    setChapters(newChapters);
  };

  const handleChapterFileChange = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/x-m4a'];
      if (validTypes.includes(file.type) || file.name.match(/\.(mp3|wav|ogg|m4a)$/i)) {
        const newChapters = [...chapters];
        newChapters[index].file = file;
        // Auto-fill chapter title from filename if empty
        if (!newChapters[index].title) {
          newChapters[index].title = file.name.replace(/\.(mp3|wav|ogg|m4a)$/i, '').replace(/_/g, ' ');
        }
        setChapters(newChapters);
      } else {
        toast({
          title: 'Invalid file',
          description: 'Please upload an audio file (MP3, WAV, OGG, M4A).',
          variant: 'destructive',
        });
      }
    }
  };

  const addChapter = () => {
    setChapters([...chapters, { title: '', file: null }]);
  };

  const removeChapter = (index) => {
    if (chapters.length > 1) {
      const newChapters = chapters.filter((_, i) => i !== index);
      setChapters(newChapters);
    }
  };

  const moveChapter = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < chapters.length) {
      const newChapters = [...chapters];
      [newChapters[index], newChapters[newIndex]] = [newChapters[newIndex], newChapters[index]];
      setChapters(newChapters);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.author) {
      toast({
        title: 'Missing Information',
        description: 'Please provide at least a title and author.',
        variant: 'destructive',
      });
      return;
    }

    if (!coverImage) {
      toast({
        title: 'Cover Required',
        description: 'Please upload a cover image for the audiobook.',
        variant: 'destructive',
      });
      return;
    }

    const validChapters = chapters.filter(ch => ch.file);
    if (validChapters.length === 0) {
      toast({
        title: 'Audio Required',
        description: 'Please upload at least one audio file.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('token');
      const uploadData = new FormData();
      
      // Add form fields
      uploadData.append('title', formData.title);
      uploadData.append('author', formData.author);
      uploadData.append('description', formData.description || '');
      uploadData.append('genres', formData.genres || 'Audiobook');
      uploadData.append('narrator', formData.narrator || '');
      
      // Add cover
      uploadData.append('cover', coverImage);
      
      // Add chapters info as JSON
      const chaptersInfo = validChapters.map((ch, idx) => ({
        title: ch.title || `Chapter ${idx + 1}`,
        order: idx
      }));
      uploadData.append('chapters_info', JSON.stringify(chaptersInfo));
      
      // Add audio files
      validChapters.forEach((chapter, index) => {
        uploadData.append(`chapter_${index}`, chapter.file);
      });

      await axios.post(`${API}/audiobooks/upload`, uploadData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      toast({
        title: 'Audiobook uploaded successfully!',
        description: `${validChapters.length} chapter(s) uploaded.`,
      });
      
      window.location.href = '/';
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload Failed',
        description: error.response?.data?.detail || 'Failed to upload audiobook. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const totalSize = chapters.reduce((acc, ch) => acc + (ch.file?.size || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black pt-20 pb-12">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-blue-600/15 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s' }} />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-pink-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s' }} />
      </div>

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-white hover:text-gray-300 transition-colors mb-4 inline-flex items-center"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back
          </button>
          
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-3 bg-purple-600/20 rounded-xl">
              <Headphones className="h-10 w-10 text-purple-400" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white">Upload Audiobook</h1>
              <p className="text-gray-400">Add audiobook with multiple chapters</p>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex space-x-2 mt-6">
            <Button
              onClick={() => navigate('/upload')}
              variant="outline"
              className="border-white/20 text-gray-400 hover:text-white hover:bg-white/10"
            >
              <UploadIcon className="h-4 w-4 mr-2" />
              Upload Book
            </Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Headphones className="h-4 w-4 mr-2" />
              Upload Audiobook
            </Button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Cover Image Section */}
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
              <ImageIcon className="h-5 w-5 mr-2 text-purple-400" />
              Cover Design
            </h2>
            
            <div className="flex flex-col md:flex-row gap-6">
              {/* Cover Preview */}
              <div className="flex-shrink-0">
                <div className="w-48 h-48 bg-white/5 rounded-lg border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden">
                  {coverPreview ? (
                    <img src={coverPreview} alt="Cover" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center text-gray-500">
                      <ImageIcon className="h-12 w-12 mx-auto mb-2" />
                      <span className="text-sm">Cover Preview</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Upload Area */}
              <div className="flex-1">
                <Label className="text-white mb-2 block">Upload Cover Image *</Label>
                <div className="relative">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverChange}
                    className="hidden"
                    id="cover-upload"
                  />
                  <label
                    htmlFor="cover-upload"
                    className="flex items-center justify-center w-full h-32 border-2 border-dashed border-purple-500/50 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-500/5 transition-all"
                  >
                    <div className="text-center">
                      <UploadIcon className="h-8 w-8 text-purple-400 mx-auto mb-2" />
                      <span className="text-purple-400">Click to upload cover image</span>
                      <p className="text-xs text-gray-500 mt-1">PNG, JPG, WEBP (recommended 400x600)</p>
                    </div>
                  </label>
                </div>
                {coverImage && (
                  <p className="text-green-400 text-sm mt-2 flex items-center">
                    ✓ {coverImage.name}
                    <button 
                      type="button" 
                      onClick={() => { setCoverImage(null); setCoverPreview(null); }}
                      className="ml-2 text-red-400 hover:text-red-300"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Chapters Section */}
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white flex items-center">
                <Music className="h-5 w-5 mr-2 text-purple-400" />
                Audio Chapters ({chapters.filter(c => c.file).length} files)
              </h2>
              <Button
                type="button"
                onClick={addChapter}
                variant="outline"
                size="sm"
                className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Chapter
              </Button>
            </div>
            
            <div className="space-y-4">
              {chapters.map((chapter, index) => (
                <div 
                  key={chapter.id || chapter._id || `chapter-${chapter.title || chapter.file_name || ''}-${index}`} 
                  className="p-4 bg-white/5 rounded-lg border border-white/10 hover:border-purple-500/30 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    {/* Drag Handle */}
                    <div className="flex flex-col items-center pt-2 text-gray-500">
                      <span className="text-xs font-mono mb-1">{index + 1}</span>
                      <div className="flex flex-col gap-1">
                        <button
                          type="button"
                          onClick={() => moveChapter(index, -1)}
                          disabled={index === 0}
                          className="p-1 hover:bg-white/10 rounded disabled:opacity-30"
                        >
                          <ChevronUp className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveChapter(index, 1)}
                          disabled={index === chapters.length - 1}
                          className="p-1 hover:bg-white/10 rounded disabled:opacity-30"
                        >
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      </div>
                    </div>

                    <div className="flex-1 space-y-3">
                      {/* Chapter Title */}
                      <Input
                        placeholder={`Chapter ${index + 1} title (optional)`}
                        value={chapter.title}
                        onChange={(e) => handleChapterTitleChange(index, e.target.value)}
                        className="bg-white/5 border-white/20 text-white placeholder:text-gray-500"
                      />

                      {/* Audio File */}
                      <div className="relative">
                        <Input
                          type="file"
                          accept="audio/*,.mp3,.wav,.ogg,.m4a"
                          onChange={(e) => handleChapterFileChange(index, e)}
                          className="hidden"
                          id={`chapter-audio-${index}`}
                        />
                        <label
                          htmlFor={`chapter-audio-${index}`}
                          className={`flex items-center justify-center w-full h-16 border-2 border-dashed rounded-lg cursor-pointer transition-all ${
                            chapter.file 
                              ? 'border-green-500/50 bg-green-500/5' 
                              : 'border-purple-500/30 hover:border-purple-400 hover:bg-purple-500/5'
                          }`}
                        >
                          {chapter.file ? (
                            <div className="flex items-center text-green-400">
                              <Music className="h-5 w-5 mr-2" />
                              <span className="truncate max-w-[200px]">{chapter.file.name}</span>
                              <span className="text-green-300 ml-2 text-sm">
                                ({(chapter.file.size / (1024 * 1024)).toFixed(1)} MB)
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center text-purple-400">
                              <UploadIcon className="h-5 w-5 mr-2" />
                              <span>Upload audio file</span>
                            </div>
                          )}
                        </label>
                      </div>
                    </div>

                    {/* Remove Button */}
                    {chapters.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeChapter(index)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {totalSize > 0 && (
              <p className="text-gray-400 text-sm mt-4">
                Total size: {(totalSize / (1024 * 1024)).toFixed(1)} MB
              </p>
            )}
          </div>

          {/* Audiobook Details */}
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-semibold text-white mb-4">Audiobook Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="title" className="text-white">Title *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter audiobook title"
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="author" className="text-white">Author *</Label>
                <Input
                  id="author"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  placeholder="Enter author name"
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="narrator" className="text-white">Narrator</Label>
                <Input
                  id="narrator"
                  name="narrator"
                  value={formData.narrator}
                  onChange={handleInputChange}
                  placeholder="Enter narrator name"
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="genres" className="text-white">Genres</Label>
                <Input
                  id="genres"
                  name="genres"
                  value={formData.genres}
                  onChange={handleInputChange}
                  placeholder="e.g., Fiction, Mystery, Romance"
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="description" className="text-white">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter audiobook description..."
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 min-h-[120px]"
                />
              </div>
            </div>
          </div>

          {/* Upload Progress */}
          {loading && uploadProgress > 0 && (
            <div className="bg-purple-900/30 rounded-lg p-4 border border-purple-500/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-purple-300">Uploading...</span>
                <span className="text-purple-300">{uploadProgress}%</span>
              </div>
              <div className="w-full h-2 bg-purple-900 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-white font-semibold py-6 text-lg shadow-lg shadow-purple-600/30"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Uploading Audiobook...
              </>
            ) : (
              <>
                <UploadIcon className="h-5 w-5 mr-2" />
                Upload Audiobook ({chapters.filter(c => c.file).length} chapters)
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

// Missing icons import
const ChevronUp = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="18 15 12 9 6 15"></polyline>
  </svg>
);

const ChevronDown = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

export default UploadAudiobook;
